import get from 'lodash.get';

import * as Actions from '../../store/shipstation-connect/ss-connect.actions';
import { PusherService } from '../../services/pusher-service';
import { DocumentType } from '../../enums/document-type';
import { LogService } from '../log-service';

export const CONNECT_PRINT_JOB_RESPONSE = {
  STATUS_JOB_ACCEPTED: 'job:accepted',
  STATUS_PRINTER_MISSING: 'printer:missing',
  STATUS_DOWNLOAD_FAILED: 'download:failed',
  STATUS_PRINTER_FAILED: 'printer:failed',
  STATUS_JOB_COMPLETE: 'job:complete',
  STATUS_JOB_FAILED: 'job:failed',
};

export const PRINT_JOB_STATUS = {
  STATUS_REQUESTED: 'requested',
  STATUS_PENDING: 'pending',
  STATUS_SUCCESS: 'success',
  STATUS_FAILURE: 'failed',
};

const mapRawPrinterFields = (printer, workstationId) => {
  return {
    id: printer.i,
    name: printer.n,
    shared: printer.s === 1,
    disabled: printer.d === 1,
    ssTimeout: printer.st,
    deviceTimeout: printer.dt,
    zplPrintSpeed: printer.zps,
    zplChunkSize: printer.zcs,
    zplInvert: printer.zi === 1,
    zplPrintSpeedRemoval: printer.zpsd === 1,
    workstationId,
  };
};

const CONNECT_BROADCAST_PRINTERS = {
  eventName: 'client-connect:broadcast:printers',
  actionToDispatch: (pusherMessage) => {
    const { printers, workstationId } = pusherMessage;
    const humanFriendlyPrinters = printers.map((printer) => mapRawPrinterFields(printer, workstationId));
    return Actions.updatePrintersSuccess(workstationId, humanFriendlyPrinters);
  },
  shouldIgnoreNotForMe: true,
};

const CONNECT_PRINTER_JOB_RESPONSE = {
  eventName: 'client-connect:response:printer:job',
  actionToDispatch: (pusherMessage) => {
    const { error, message, requestId, status, workstationId } = pusherMessage;
    if (error) {
      LogService.logInfo('SSConnect print request failure', pusherMessage);
      return Actions.printDocumentFailure({
        error,
        errorMessage: message,
        requestId,
        workstationId,
      });
    }
    if (status === CONNECT_PRINT_JOB_RESPONSE.STATUS_JOB_ACCEPTED) {
      return Actions.printDocumentPending(requestId);
    }
    return Actions.printDocumentSuccess(requestId);
  },
};

const CONNECT_BROADCAST_PRINTERS_ERROR = {
  eventName: 'client-connect:broadcast:malfunction:printers',
  actionToDispatch: (pusherMessage) => {
    const { error } = pusherMessage;
    return Actions.updatePrintersFailure(error);
  },
};

export const ConnectPrinterEvents = [CONNECT_BROADCAST_PRINTERS, CONNECT_PRINTER_JOB_RESPONSE, CONNECT_BROADCAST_PRINTERS_ERROR];

export const docTypeToGetPusherArguments = {
  [DocumentType.PickList.identifier]: (ids, _, printJob) => ({
    fulfillmentPlanIds: ids,
    path: printJob.printUrl || DocumentType.PickList.apiDownloadPath,
    DocTemplate: 'other',
    RecordContext: 1,
  }),
  [DocumentType.BatchPickList.identifier]: (ids, _, printJob) => ({
    fulfillmentPlanSetIds: ids,
    path: printJob.printUrl || DocumentType.BatchPickList.apiDownloadPath,
    DocTemplate: 'other',
    RecordContext: 1,
  }),
  [DocumentType.PackingSlip.identifier]: (ids, templateId, printJob) => {
    return {
      fulfillmentplanids: ids,
      path: printJob.printUrl || DocumentType.PackingSlip.apiDownloadPath,
      packingsliptemplateid: templateId,
      DocTemplate: 'packing',
      recordContext: 1,
    };
  },
  [DocumentType.Label.identifier]: (ids) => ({
    ids,
    shipmentIds: ids,
    ShipmentID: ids,
    RecordContext: 2,
    path: `${DocumentType.Label.apiDownloadPath}/${ids}`,
    DocTemplate: 'label',
  }),
  [DocumentType.CustomsForm.identifier]: (ids) => ({
    fulfillmentplanids: ids,
    RecordContext: 1,
    path: '/api/download/form/fulfillmentplan', // EJG: does not match apiDownloadPath in DocumentType enum
    DocTemplate: 'other',
  }),
  [DocumentType.EndOfDayForm.identifier]: (scanFormIds) => ({
    eodFormIds: scanFormIds,
    download: true,
    path: DocumentType.EndOfDayForm.apiDownloadPath,
    DocTemplate: 'eod',
  }),
  [DocumentType.BatchLabel.identifier]: (ids, templateId, printJob) => {
    const url = get(printJob, 'printUrl', '');
    const params = url.substring(url.indexOf('?')).split('&');
    const excludeAlreadyPrintedParam = params.find((param) => param.includes('excludeAlreadyPrinted'));
    return {
      fulfillmentPlanSetIds: ids,
      excludeAlreadyPrinted: excludeAlreadyPrintedParam ? excludeAlreadyPrintedParam.split('=')[1] : false,
      markAsPrinted: true,
      path: DocumentType.BatchLabel.apiDownloadPath,
      DocTemplate: 'label',
      templateId: undefined,
    };
  },
  [DocumentType.BatchPackingSlip.identifier]: (id, templateId, printJob) => {
    const url = get(printJob, 'printUrl', '');
    const params = url.substring(url.indexOf('?')).split('&');
    const excludeAlreadyPrintedParam = params.find((param) => param.includes('excludeAlreadyPrinted'));
    return {
      fulfillmentPlanSetIds: id,
      excludeAlreadyPrinted: excludeAlreadyPrintedParam ? excludeAlreadyPrintedParam.split('=')[1] : false,
      markAsPrinted: true,
      path: url || DocumentType.BatchPackingSlip.apiDownloadPath,
      packingsliptemplateid: templateId,
      DocTemplate: 'packing',
      recordContext: 1,
    };
  },
  [DocumentType.OrderSummary.identifier]: (id, templateId, printJob) => {
    const url = get(printJob, 'printUrl', '');
    return {
      markAsPrinted: false,
      path: url,
      DocTemplate: 'other',
    };
  },
  [DocumentType.HotkeysAndBarcode.identifier]: (id, templateId, printJob) => {
    const url = get(printJob, 'printUrl', '');
    const keyPrefix = url.split('=')[1] || 'Ctrl';
    return {
      path: url,
      DocTemplate: 'other',
      keyPrefix,
    };
  },
  [DocumentType.ShipmentManifest.identifier]: (ids) => ({
    fulfillmentPlanIds: ids,
    path: DocumentType.ShipmentManifest.apiDownloadPath,
  }),
  [DocumentType.BatchShipmentManifest.identifier]: (ids) => ({
    fulfillmentPlanSetIds: ids,
    path: DocumentType.BatchShipmentManifest.apiDownloadPath,
  }),
  [DocumentType.LocationBarcode.identifier]: (_, __, printJob) => ({
    path: printJob.printUrl || DocumentType.LocationBarcode.apiDownloadPath,
    DocTemplate: 'other',
  }),
};

class ConnectPrintersClass {
  printDocument(printJob) {
    const { requestId, printer, documentType, ids, templateId, testOrderId } = printJob;

    const defaultArguments = {
      printer: null,
      printerId: !printer ? 'NO ID' : printer.id,
      asattachment: true,
      testOrderId,
      workstationId: !printer ? 'NO ID' : printer.workstationId,
      requestId,
      BatchID: null, // ???
      userId: this.userId,
    };

    const docTypePusherArgumentsFunction = docTypeToGetPusherArguments[documentType];
    if (!docTypePusherArgumentsFunction) {
      return;
    }

    const pusherArguments = {
      ...defaultArguments,
      ...docTypePusherArgumentsFunction(ids, templateId, printJob),
    };

    return PusherService.push(
      'connect:request:printer:job',
      pusherArguments,
      PusherService.getWorkstationChannelName(pusherArguments.workstationId),
    );
  }
}

export const ConnectPrinters = new ConnectPrintersClass();
